import { expectedDifficultyCounts, type Difficulty } from "./uasa.js";
import type { BlueprintSlot, CurriculumStandard, PaperBlueprint, PaperLanguage, PaperSettings } from "./exam.js";

/**
 * Practice papers built from imported DSKP standards (any subject, KSSR Tahun 1–6 / KSSM Tingkatan 1–5).
 * The blueprint is deterministic: which SP, format, marks and difficulty each question has. The AI only
 * writes the questions. SP codes always come from curriculum_standards — never from the AI or the user.
 */
export interface SubjectProfile {
  nameMs: string;
  nameEn: string;
  /** Share of questions that are objective (4-option). The rest are short structured answers. */
  objektifShare: number;
  /** Numeric answers get a machine-checked arithmetic expression. */
  numeric: boolean;
  /** Languages a paper may be written in for this subject. */
  languages: readonly PaperLanguage[];
}

export const SUBJECT_PROFILES: Readonly<Record<string, SubjectProfile>> = {
  bahasa_melayu: { nameMs: "Bahasa Melayu", nameEn: "Malay Language", objektifShare: 0.6, numeric: false, languages: ["ms"] },
  bahasa_inggeris: { nameMs: "Bahasa Inggeris", nameEn: "English Language", objektifShare: 0.6, numeric: false, languages: ["en"] },
  matematik: { nameMs: "Matematik", nameEn: "Mathematics", objektifShare: 0.5, numeric: true, languages: ["ms", "en", "dlp"] },
  sains: { nameMs: "Sains", nameEn: "Science", objektifShare: 0.6, numeric: false, languages: ["ms", "en", "dlp"] },
  sejarah: { nameMs: "Sejarah", nameEn: "History", objektifShare: 0.6, numeric: false, languages: ["ms"] },
};

const FALLBACK: SubjectProfile = { nameMs: "Subjek", nameEn: "Subject", objektifShare: 0.6, numeric: false, languages: ["ms"] };
export const subjectProfile = (slug: string): SubjectProfile => SUBJECT_PROFILES[slug] ?? FALLBACK;

export const PRACTICE_LIMITS = { MIN_QUESTIONS: 5, MAX_QUESTIONS: 30, MAX_STANDARDS: 12 } as const;

export function levelLabel(level: "rendah" | "menengah" | undefined, year: number, lang: PaperLanguage): string {
  if (lang === "en") return level === "menengah" ? `Form ${year}` : `Year ${year}`;
  return level === "menengah" ? `Tingkatan ${year}` : `Tahun ${year}`;
}

/** Plan a DSKP practice paper. Section A = objective (1 mark), section B = structured (2–3 marks). */
export function buildPracticeBlueprint(s: PaperSettings, available: readonly CurriculumStandard[]): PaperBlueprint {
  if (s.mode !== "dskp" || !s.subject) throw new RangeError("Tetapan DSKP tidak lengkap");
  const wanted = s.spCodes ?? [];
  if (wanted.length === 0) throw new RangeError("Pilih sekurang-kurangnya satu Standard Pembelajaran");
  if (wanted.length > PRACTICE_LIMITS.MAX_STANDARDS) throw new RangeError(`Maksimum ${PRACTICE_LIMITS.MAX_STANDARDS} SP`);
  const byCode = new Map(available.map((x) => [x.spCode, x]));
  const chosen: CurriculumStandard[] = [];
  for (const code of wanted) {
    const st = byCode.get(code);
    if (!st) throw new RangeError(`SP ${code} tiada dalam DSKP`);
    if (!chosen.includes(st)) chosen.push(st);
  }
  const n = s.questionCount ?? 10;
  if (!Number.isInteger(n) || n < PRACTICE_LIMITS.MIN_QUESTIONS || n > PRACTICE_LIMITS.MAX_QUESTIONS) {
    throw new RangeError(`Bilangan soalan mesti ${PRACTICE_LIMITS.MIN_QUESTIONS}–${PRACTICE_LIMITS.MAX_QUESTIONS}`);
  }
  const profile = subjectProfile(s.subject);
  const [lo, mid, hi] = expectedDifficultyCounts(s.difficultyRatio ?? [5, 3, 2], n);
  const diffs: Difficulty[] = [...Array(lo).fill("rendah"), ...Array(mid).fill("sederhana"), ...Array(hi).fill("tinggi")];
  // Objective questions take the easier end; every "tinggi" question is structured.
  const nObj = Math.min(Math.round(n * profile.objektifShare), n - hi);
  const slots: BlueprintSlot[] = [];
  for (let i = 0; i < n; i++) {
    const st = chosen[i % chosen.length]!;
    const objektif = i < nObj;
    const d = diffs[i]!;
    slots.push({
      section: objektif ? "A" : "B",
      number: objektif ? i + 1 : i - nObj + 1,
      marks: objektif ? 1 : d === "tinggi" ? 3 : 2,
      difficulty: d,
      topic: st.topic ?? st.skDesc ?? st.skCode,
      spCode: st.spCode,
      type: objektif ? "objektif" : "subjektif",
    });
  }
  const name = s.language === "en" ? profile.nameEn : profile.nameMs;
  const prefix = s.language === "en" ? "Practice" : "Latihan";
  return {
    title: `${prefix} ${name} ${levelLabel(s.level, s.year, s.language)}`,
    totalMarks: slots.reduce((a, x) => a + x.marks, 0),
    durationMinutes: null,
    slots,
    subject: s.subject,
    standards: chosen,
  };
}
